// src/pages/Accueil.js
// Endless scroll : une carte à la fois, catégories mélangées en aléatoire.
// Nouvelles cartes chargées automatiquement en approchant du bas.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { drawOne } from '../data/cardsApi';
import { Card } from '../components/Card';
import { HeartIcon } from '../components/Icon';
import { C, GRADIENTS, FONTS } from '../styles/theme';

// Catégories disponibles pour le scroll — modifie cette liste pour ajouter/retirer
const FEED_CATEGORIES = [
  { type: 'action',   context: 'maison',   icon: '♟️', label: 'Action' },
  { type: 'verite',   context: 'maison',   icon: '🎭', label: 'Vérité' },
  { type: 'punition', context: 'maison',   icon: '😈', label: 'Punition' },
  { type: 'date',     context: 'general',  icon: '🌙', label: 'Date' },
  { type: 'conseil',  context: 'general',  icon: '💡', label: 'Conseil' },
];

const BATCH_SIZE = 6;      // cartes chargées à chaque fois
const LOAD_THRESHOLD = 300; // pixels avant le bas pour déclencher le chargement

// Génère un lot de cartes en alternant les catégories de façon aléatoire.
// Les cartes avec has_target=true sont exclues du feed (noms manquants = bizarre).
function generateBatch(maxIntensity = 2) {
  const shuffled = [...FEED_CATEGORIES].sort(() => Math.random() - 0.5);
  const cards = [];

  for (let i = 0; i < BATCH_SIZE; i++) {
    const cat = shuffled[i % shuffled.length];
    const card = drawOne({
      type: cat.type,
      context: cat.context,
      maxIntensity,
      hasTarget: false,   // ← exclut toutes les cartes qui visent quelqu'un
    });
    if (card) {
      // Clé unique pour React (id + timestamp pour permettre la même carte plusieurs fois)
      cards.push({ ...card, _feedKey: `${card.id}-${Date.now()}-${i}` });
    }
  }
  return cards;
}

export function Accueil({ onNav, favs, onFav }) {
  const [feed,      setFeed]      = useState(() => generateBatch());
  const [loading,   setLoading]   = useState(false);
  const [intensity, setIntensity] = useState(2);
  const bottomRef = useRef(null);

  // Charge un nouveau lot de cartes
  const loadMore = useCallback(() => {
    if (loading) return;
    setLoading(true);
    // Léger délai pour ne pas bloquer le rendu
    setTimeout(() => {
      setFeed(prev => [...prev, ...generateBatch(intensity)]);
      setLoading(false);
    }, 200);
  }, [loading, intensity]);

  // Intersection Observer : déclenche loadMore quand on approche du bas
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: `${LOAD_THRESHOLD}px` }
    );
    if (bottomRef.current) observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  // Recharge le feed quand l'intensité change
  useEffect(() => {
    setFeed(generateBatch(intensity));
  }, [intensity]);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', paddingBottom: 90 }}>

      {/* ── Header ── */}
      <div style={{
        padding: '52px 20px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ fontFamily: FONTS.display, fontSize: 30, color: C.text, fontStyle: 'italic' }}>
          Messy
        </div>
        <button onClick={() => onNav('favoris')} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: `1px solid ${C.sand}`,
          borderRadius: 50, padding: '8px 14px',
          color: C.roseDark, fontWeight: '500', fontSize: 13, cursor: 'pointer',
        }}>
          <HeartIcon filled={favs.length > 0} size={16} />
          Favoris
          {favs.length > 0 && (
            <span style={{
              background: C.rose, color: '#fff', borderRadius: '50%',
              width: 18, height: 18, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 10, fontWeight: '700',
            }}>{favs.length}</span>
          )}
        </button>
      </div>

      {/* ── CTA Jouer ── */}
      <div style={{ padding: '0 20px 28px' }}>
        <button onClick={() => onNav('jeux')} style={{
          width: '100%', padding: '20px', border: 'none', borderRadius: 20,
          background: GRADIENTS.primary, cursor: 'pointer',
          fontFamily: FONTS.display, fontSize: 26, color: '#fff', fontStyle: 'italic',
          boxShadow: '0 8px 32px rgba(232,164,164,0.35)',
        }}>
          Jouer ensemble ✦
        </button>
      </div>

      {/* ── Filtre intensité ── */}
      <div style={{ padding: '0 20px 24px' }}>
        <div style={{ fontSize: 11, fontWeight: '600', letterSpacing: '0.1em', color: C.textLight, textTransform: 'uppercase', marginBottom: 10 }}>
          Intensité du feed
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { val: 1, icon: '🌿', label: 'Doux' },
            { val: 2, icon: '🌶️', label: 'Pimenté' },
            { val: 3, icon: '🔥', label: 'Intense' },
          ].map(opt => (
            <button key={opt.val} onClick={() => setIntensity(opt.val)} style={{
              flex: 1, padding: '10px 6px', borderRadius: 14, border: 'none', cursor: 'pointer',
              background: intensity >= opt.val ? C[['sage','peach','rose'][opt.val-1]] : C.bgMuted,
              transition: 'all 0.2s',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            }}>
              <span style={{ fontSize: 18 }}>{opt.icon}</span>
              <span style={{ fontSize: 11, fontWeight: '600', color: intensity >= opt.val ? C.text : C.textLight }}>
                {opt.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Séparateur ── */}
      <div style={{ height: 1, background: C.sand, margin: '0 20px 28px', opacity: 0.6 }} />

      {/* ── Feed de cartes ── */}
      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {feed.map((card, i) => (
          <div key={card._feedKey} className="anim-card-in" style={{ animationDelay: `${(i % BATCH_SIZE) * 0.05}s` }}>
            <Card
              card={card}
              isFav={favs.some(f => f.id === card.id)}
              onFav={onFav}
            />
          </div>
        ))}
      </div>

      {/* ── Spinner de chargement ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: C.textLight, fontSize: 13, fontStyle: 'italic' }}>
          Chargement…
        </div>
      )}

      {/* ── Sentinelle pour l'Intersection Observer ── */}
      <div ref={bottomRef} style={{ height: 1 }} />
    </div>
  );
}
